import toast from 'react-hot-toast';
import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  TableListData,
} from 'components';
import { Button, InputWraper, useForm, IUseFormStructureItem } from 'modules';
import React, { useCallback, useState } from 'react';
import {
  useGetAuditLogsAdminExportQuery,
  useGetAdminPermissionsQuery,
} from 'services';
import type { GetListAuditLogEntity } from 'services/openapi';

import {
  columnsDefined,
  convertVariables,
  type FilterType,
  getInitFilterValues,
} from './config';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import {
  GetAuditLogsAdminDTO,
  useGetAuditLogsAdminQuery,
} from 'services/user/queries/useGetAuditLogsAdminQuery';

const AuditLogsAdminList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  console.log('initFilterValues', initFilterValues);
  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const { data: dataScreenActionPermissions } = useGetAdminPermissionsQuery();

  const dataScreenActionPermissionsLst = useCallback(() => {
    const data = dataScreenActionPermissions;

    if (!data) return [];
    const lstRess = [
      {
        label: 'Tất cả',
        value: 'Tất cả',
      },
    ];
    return [
      ...lstRess,
      ...dataScreenActionPermissions?.data?.map((item: any) => ({
        label: item,
        value: item,
      })),
    ];
  }, [dataScreenActionPermissions]);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'actorId',
      label: 'Admin',
      defaultValue: initFilterValues.actorId,
    },
    {
      name: 'action',
      label: 'Action',
      defaultValue: initFilterValues.action,
    },
    {
      name: 'startDate',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.startDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.endDate).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.endDate);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'endDate',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.endDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.startDate).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.startDate);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch } =
    useGetAuditLogsAdminExportQuery({
      ...convertVariables(variables),
      page: '1',
      pageSize: String((dataTable as unknown as GetListAuditLogEntity)?.total),
    });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    try {
      const allData = await refetch({ throwOnError: true });
      await saveListExcel(
        allData.data?.data,
        columnsDefined as ColumnDef<GetAuditLogsAdminDTO>[],
        'Danh_sach_Logs_Admin',
        true,
        {},
      );
    } catch (error) {
      if (!isRequestError(error))
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
    }
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('actorId')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('action')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={dataScreenActionPermissionsLst()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('startDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('endDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<GetAuditLogsAdminDTO>
        columns={columnsDefined as ColumnDef<GetAuditLogsAdminDTO>[]}
        useQuery={useGetAuditLogsAdminQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        orderColumn
        callBackData={setDataTable}
      />
    </>
  );
};

export default AuditLogsAdminList;
